import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, Edit } from "lucide-react";
import {
  getUserByid,
  manualVerifyPlaidBankAccount,
  manualVerifyPlaidIdentity,
  updateUserProfile,
} from "../../../reducers/authentication.slice";
import { getUsers } from "../../../utils/getUser";
import { convertDateFormatInUtc } from "../../../utils/util";
import { AuthContext } from "../../../context/authContext";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(name) {
  const parts = name.split(" ");
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`;
}

const PERMISSIONS = [
  { name: "dashboard", label: "Dashboard" },
  { name: "shipping_and_tracking", label: "Shipping & Tracking" },
  { name: "live_orders", label: "Live Orders" },
  { name: "completed_orders", label: "Completed Orders" },
  { name: "pending_rsc_quotes", label: "Pending RSC Quotes" },
  { name: "pre_quoted_cooks", label: "Sell Catalog" },
  { name: "in_store_flips", label: "In Store Flips" },
  { name: "user_management", label: "User Management" },
  { name: "analytics", label: "Analytics" },
  { name: "terms_and_conditions", label: "Terms and Conditions" },
];

const InfoRow = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
    <span style={{ fontSize: 15, color: "gray", flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 15, color: "white", textAlign: "right" }}>{children}</span>
  </div>
);

const UserViewPage = ({ dialogMode = false, userId: userIdProp, onEdit, onUpdated }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const [user, setUser] = useState({});
  const [isRefresh, setIsRefresh] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [manualVerifyAccount, setManualVerifyAccount] = useState(null);
  const [manualVerifyIdentityOpen, setManualVerifyIdentityOpen] = useState(false);

  const currentUser = getUsers();
  const canEditManagedUser = currentUser?.permission_details?.access_level !== "READ";
  const achPaymentMethods = user?.payment_methods?.filter((item) => item?.payment_method === "ACH") || [];

  useEffect(() => {
    setShowLoader(true);
    const userId = userIdProp || location.state.userId;
    dispatch(getUserByid({ userId })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setUser(res?.payload);
      setShowLoader(false);
    });
  }, [isRefresh, userIdProp]);

  const refreshUserAndParent = () => {
    setIsRefresh((r) => !r);
    if (onUpdated) onUpdated();
  };

  const handleSuspendUser = () => {
    setShowLoader(true);
    dispatch(updateUserProfile({ status: "SUSPENDED", id: user?.id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      refreshUserAndParent();
      showToast("User is suspended", "success");
    });
  };

  const handleActiveUser = () => {
    setShowLoader(true);
    dispatch(updateUserProfile({ status: "ACTIVE", id: user?.id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      refreshUserAndParent();
      setShowLoader(false);
      showToast("User is Activated", "success");
    });
  };

  const handleManualVerifyIdentity = () => {
    setShowLoader(true);
    dispatch(manualVerifyPlaidIdentity({ user_id: user?.id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setManualVerifyIdentityOpen(false);
      refreshUserAndParent();
      setShowLoader(false);
      showToast("Identity manually verified", "success");
    });
  };

  const handleManualVerifyBankAccount = () => {
    if (!manualVerifyAccount) return;
    setShowLoader(true);
    dispatch(manualVerifyPlaidBankAccount({ user_id: user?.id, payment_method_id: manualVerifyAccount?.id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setManualVerifyAccount(null);
      refreshUserAndParent();
      setShowLoader(false);
      showToast("Bank account manually verified", "success");
    });
  };

  const avatarName = `${user?.first_name || "?"} ${user?.last_name || "?"}`;
  const fullName =
    user?.first_name || user?.last_name
      ? `${user?.first_name || ""} ${user?.last_name || ""}`.trim()
      : null;

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: dialogMode ? 0 : "24px 16px 48px", width: "100%" }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        {!dialogMode && (
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#ffffff85",
              background: "none",
              border: "none",
              cursor: "pointer",
              marginBottom: 16,
              padding: 0,
              fontSize: 14,
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
        )}

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{ fontSize: "1.3rem", fontWeight: "bold", margin: 0 }}>My Profile</h2>
          {user?.role !== "MEMBER" && canEditManagedUser && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                if (dialogMode && onEdit) onEdit(user);
                else navigate("/dashboard/user-management/edit-profile", { state: { userDetails: user } });
              }}
            >
              <Edit size={18} />
            </Button>
          )}
        </div>

        {/* Info card */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.05)",
            borderRadius: 8,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 14,
            marginBottom: 16,
          }}
        >
          {/* Profile Pic */}
          <InfoRow label="Profile Pic">
            {user?.profile_picture ? (
              <img
                src={user?.profile_picture}
                alt={user?.first_name}
                style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid white" }}
              />
            ) : avatarName !== "? ?" ? (
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: stringToColor(avatarName),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: 600,
                  fontSize: 13,
                  border: "2px solid white",
                }}
              >
                {getInitials(avatarName)}
              </div>
            ) : null}
          </InfoRow>

          {fullName && (
            <InfoRow label="Full Name">
              <span style={{ textTransform: "capitalize" }}>{fullName}</span>
            </InfoRow>
          )}
          {user?.email && <InfoRow label="Email">{user?.email}</InfoRow>}
          <InfoRow label="IP Address">{user?.ip_address || "-"}</InfoRow>

          <InfoRow label="Status">
            {user?.is_verified ? (
              user?.status && (
                <span
                  style={{
                    fontSize: 14,
                    color: user?.status === "ACTIVE" ? "#4caf50" : "#f44336",
                    backgroundColor:
                      user?.status === "ACTIVE" ? "rgba(76,175,79,0.3)" : "rgba(244,67,54,0.3)",
                    padding: "5px 10px",
                    borderRadius: 5,
                    textTransform: "capitalize",
                  }}
                >
                  {user?.status}
                </span>
              )
            ) : (
              <span
                style={{
                  fontSize: 14,
                  color: "white",
                  backgroundColor: "rgba(116,116,116,0.3)",
                  padding: "5px 10px",
                  borderRadius: 5,
                }}
              >
                Not Verified
              </span>
            )}
          </InfoRow>

          <InfoRow label="Login Type">
            <span style={{ textTransform: "capitalize" }}>{user?.login_type || "-"}</span>
          </InfoRow>
          <InfoRow label="Discord Username">
            <span style={{ textTransform: "capitalize" }}>{user?.discord_id || "-"}</span>
          </InfoRow>
          <InfoRow label="Discord Role">
            <span style={{ textTransform: "capitalize" }}>{user?.discord_role || "-"}</span>
          </InfoRow>

          {/* Plaid ID Verification */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div>
              <div style={{ fontSize: 15, color: "gray" }}>Plaid ID Verification</div>
              {!user?.is_plaid_verified && (
                <div style={{ fontSize: 13, color: "gray" }}>Not Verified</div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {user?.is_plaid_verified && (
                <span
                  style={{
                    fontSize: 14,
                    color: "#4caf50",
                    backgroundColor: "rgba(76,175,79,0.3)",
                    padding: "5px 10px",
                    borderRadius: 5,
                  }}
                >
                  Verified
                </span>
              )}
              {!user?.is_plaid_verified && canEditManagedUser && (
                <Button size="sm" onClick={() => setManualVerifyIdentityOpen(true)}>
                  Manual Verify
                </Button>
              )}
            </div>
          </div>

          <InfoRow label="Signup Date">
            {user?.date_of_joining
              ? convertDateFormatInUtc(user?.date_of_joining, "MMMM D, YYYY hh:mm:ss A")
              : "-"}
          </InfoRow>
          <InfoRow label="Last Login">
            {user?.last_login
              ? convertDateFormatInUtc(user?.last_login, "MMMM D, YYYY hh:mm:ss A")
              : "-"}
          </InfoRow>
        </div>

        {/* Bank Accounts */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: "1.3rem", fontWeight: "bold", marginBottom: 8 }}>Bank Accounts</h3>
          {achPaymentMethods?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {achPaymentMethods.map((paymentMethod) => (
                <div
                  key={paymentMethod?.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    border: "1px solid #ffffff1f",
                    borderRadius: 5,
                    padding: 10,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, color: "white" }}>
                      {paymentMethod?.bank_name || "Bank Account"}
                    </div>
                    <div style={{ fontSize: 13, color: "gray" }}>
                      Account ending {paymentMethod?.account_last_four || "N/A"}
                    </div>
                    {!paymentMethod?.is_plaid_bank_verified && (
                      <div style={{ fontSize: 13, color: "gray" }}>Not Verified</div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {paymentMethod?.is_plaid_bank_verified && (
                      <span
                        style={{
                          fontSize: 14,
                          color: "#4caf50",
                          backgroundColor: "rgba(76,175,79,0.3)",
                          padding: "5px 10px",
                          borderRadius: 5,
                        }}
                      >
                        Verified
                      </span>
                    )}
                    {!paymentMethod?.is_plaid_bank_verified && canEditManagedUser && (
                      <Button
                        size="sm"
                        disabled={!user?.is_plaid_verified}
                        onClick={() => setManualVerifyAccount(paymentMethod)}
                      >
                        Manual Verify
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 14, color: "gray" }}>No ACH bank account found</div>
          )}
        </div>

        {/* Permissions (non-MEMBER only) */}
        {user?.role !== "MEMBER" && user?.permission_details && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <h3 style={{ fontSize: "1.3rem", fontWeight: "bold", margin: 0 }}>Permissions</h3>
              <span
                style={{
                  fontSize: "0.95rem",
                  color: "white",
                  border: "1px solid #ffffff40",
                  borderRadius: 999,
                  padding: "4px 10px",
                }}
              >
                Access: {user?.permission_details?.access_level || "WRITE"}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {PERMISSIONS.map(({ name, label }) => (
                <label
                  key={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 14,
                    minWidth: "45%",
                    color: "white",
                    cursor: "default",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!user?.permission_details?.[name]}
                    disabled
                    readOnly
                    style={{ accentColor: "#00ADB5" }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          {user?.status === "ACTIVE" ? (
            <Button
              style={{ backgroundColor: "#FF1818", color: "white" }}
              onClick={handleSuspendUser}
              disabled={showLoader}
            >
              Suspend
            </Button>
          ) : (
            <Button
              style={{ backgroundColor: "#4CAF50", color: "white" }}
              onClick={handleActiveUser}
              disabled={showLoader}
            >
              Active
            </Button>
          )}
        </div>
      </div>

      {/* Manual Verify Identity dialog */}
      <Dialog open={manualVerifyIdentityOpen} onOpenChange={() => setManualVerifyIdentityOpen(false)}>
        <DialogContent style={{ maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle>Manual Verify Identity?</DialogTitle>
            <DialogDescription>This will mark the user identity as Plaid verified.</DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ justifyContent: "center", gap: 8 }}>
            <Button variant="outline" onClick={() => setManualVerifyIdentityOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleManualVerifyIdentity} disabled={showLoader}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Verify Bank Account dialog */}
      <Dialog open={Boolean(manualVerifyAccount)} onOpenChange={() => setManualVerifyAccount(null)}>
        <DialogContent style={{ maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle>Manual Verify Bank Account?</DialogTitle>
            <DialogDescription>
              This will mark the selected ACH bank account as Plaid verified.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ justifyContent: "center", gap: 8 }}>
            <Button variant="outline" onClick={() => setManualVerifyAccount(null)}>
              Cancel
            </Button>
            <Button onClick={handleManualVerifyBankAccount} disabled={showLoader}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserViewPage;
